import { useState, useMemo, useCallback, useEffect, lazy, Suspense } from 'react';
import { Link, NavLink, useNavigate, useLocation } from 'react-router-dom';
import {
  CreditCard,
  Hammer,
  Menu,
  User,
  X,
  AlertTriangle,
  Info,
  Sparkles,
  ChevronRight,
  ChevronDown,
  Bell,
  Layers,
  ShieldAlert,
  FileUp,
} from 'lucide-react';
import { useContext } from 'react';
import { UserContext } from '../../context/user/UserContext';
import { UserDetailContext } from '../../context/userDetails/UserDetailContext';
import { HealthContext } from '../../context/health/HealthContext';
import { StripeContext } from '../../context/stripe/StripeContext';
import NavbarNavigation from './components/NavbarNavigation';
import LogoSection from './components/LogoSection';
import SearchBar from './components/SearchBar';
import Account from './components/Account';
import { upgradeToArtisanApi } from '../../global-api/user.api';
import { toast } from 'sonner';
import { ArtisanContext } from '../../context/artisans/ArtisanContext';
import { NotificationContext } from '../../context/notifications/NotificationContext';
import Notifications from './components/Notifications';
import MobileMenu from './components/MobileMenu';
import UpgradeToArtisanButton from './components/UpgradeToArtisanButton';
import PageLoader from '../../ui/PageLoader';
import { motion, AnimatePresence } from 'framer-motion';
import { useVerificationCheck } from '../../global-hooks/useVerificationCheck';
import EmailVerificationModal from '../../modals/EmailVerificationModal';
import CompleteProfileModal from '../../modals/CompleteProfileModal';
import Notice from '../../modals/NoticeModal';

const UpgradeToArtisanModal = lazy(() => import('../../features/upgrade-to-artisan/UpgradeToArtisan'));

export default function NavbarLayout() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [dismissedNotices, setDismissedNotices] = useState(new Set());
  const [showCompleteProfile, setShowCompleteProfile] = useState(false);
  const [isNoticesExpanded, setIsNoticesExpanded] = useState(false);
  const [showProfileNotice, setShowProfileNotice] = useState(false);

  const toggleDismissNotice = (type) => {
    setDismissedNotices((prev) => {
      const next = new Set(prev);
      if (next.has(type)) {
        next.delete(type);
      } else {
        next.add(type);
      }
      return next;
    });
  };

  const { user, setUser, loggedIn, loading, handleLogout } =
    useContext(UserContext);

  const { userDetail, fetchUserDetail, isDetailsLoading } = useContext(UserDetailContext);

  const { serverDown, userOffline } = useContext(HealthContext);
  const { stripeStatus } = useContext(StripeContext);

  const {
    isModalOpen: isVerifyModalOpen,
    setIsModalOpen: setIsVerifyModalOpen,
    checkVerification,
    handleResend,
    loading: verifyLoading,
  } = useVerificationCheck();

  const calculateCompletion = useCallback(() => {
    if (!userDetail) return 0;
    const fields = [
      { key: 'bio', weight: 15 },
      { key: 'profile_image', weight: 15 },
      { key: 'banner_image', weight: 10 },
      { key: 'phone_number', weight: 5 },
      { key: 'country', weight: 5 },
      { key: 'experience_years', weight: 10 },
      { key: 'price_per_hour', weight: 10 },
      { key: 'skills', weight: 5 },
      { key: 'projects', weight: 25 },
    ];
    let total = 0;
    fields.forEach((f) => {
      const val = userDetail[f.key];
      if (val && val !== '' && val !== 0 && (Array.isArray(val) ? val.length > 0 : true)) {
        total += f.weight;
      }
    });
    return total;
  }, [userDetail]);

  const profileCompletion = useMemo(() => calculateCompletion(), [calculateCompletion]);

  const allNotices = useMemo(() => {
    const notices = [];

    // Local calculation to ensure it always has fresh data
    const calculateComp = () => {
      if (!userDetail) return 0;
      const fields = [
        { key: 'bio', weight: 15 },
        { key: 'banner_image', weight: 10 },
        { key: 'profile_image', weight: 15 },
        { key: 'phone_number', weight: 5 },
        { key: 'country', weight: 5 },
        { key: 'experience_years', weight: 10 },
        { key: 'price_per_hour', weight: 10 },
        { key: 'skills', weight: 5 },
        { key: 'projects', weight: 25 },
      ];
      let total = 0;
      fields.forEach((f) => {
        const val = userDetail[f.key];
        if (val && val !== '' && val !== 0 && (Array.isArray(val) ? val.length > 0 : true)) {
          total += f.weight;
        }
      });
      return total;
    };

    const comp = calculateComp();

    if (
      loggedIn &&
      (user?.active_role?.toLowerCase() === 'artisan' || user?.role?.toLowerCase()?.includes('artisan')) &&
      comp < 80
    ) {
      notices.push({
        type: 'PROFILE_INCOMPLETE',
        text: `Profile ${comp}% Complete`,
        color: 'rose',
      });
    }

    if (userOffline && !dismissedNotices.has('OFFLINE')) {
      notices.push({
        type: 'OFFLINE',
        text: 'No Internet Connection',
        color: 'rose',
      });
    }

    if (serverDown && !dismissedNotices.has('SERVER')) {
      notices.push({ type: 'SERVER', text: 'Server Offline', color: 'rose' });
    }

    if (loggedIn && user?.active_role === 'artisan') {
      if (
        (!stripeStatus?.connected || !stripeStatus?.payoutsEnabled) &&
        !stripeStatus?.loading &&
        !dismissedNotices.has('STRIPE')
      ) {
        notices.push({
          type: 'STRIPE',
          text: 'Stripe Setup Required',
          color: 'indigo',
        });
      }

      if (
        userDetail?.approval_status === 'PENDING' &&
        !dismissedNotices.has('APPROVAL')
      ) {
        notices.push({
          type: 'APPROVAL',
          text: 'Approval Pending',
          color: 'amber',
        });
      }
    }

    // Add Email Verification Notice
    if (
      loggedIn &&
      user &&
      !user.is_email_verified &&
      !dismissedNotices.has('VERIFY_EMAIL')
    ) {
      notices.push({
        type: 'VERIFY_EMAIL',
        text: 'Verify Your Email',
        color: 'rose',
      });
    }

    if (
      loggedIn &&
      user?.active_role === 'artisan' &&
      userDetail &&
      (!userDetail.government_id || !userDetail.license_certificate) &&
      !dismissedNotices.has('DOCUMENTS')
    ) {
      notices.push({
        type: 'DOCUMENTS',
        text: 'Identity Documents Missing',
        color: 'amber',
      });
    }

    return notices;
  }, [
    serverDown,
    loggedIn,
    user,
    userDetail,
    stripeStatus,
    dismissedNotices,
    userOffline,
    profileCompletion,
  ]);

  useEffect(() => {
    if (
      loggedIn &&
      user?.active_role === 'artisan' &&
      userDetail &&
      (!userDetail.government_id || !userDetail.license_certificate)
    ) {
      if (!sessionStorage.getItem('dismissedGovIdModal')) {
        setShowCompleteProfile(true);
      }
    }
  }, [loggedIn, user, userDetail]);

  const handleCloseCompleteProfile = () => {
    sessionStorage.setItem('dismissedGovIdModal', 'true');
    setShowCompleteProfile(false);
  };

  useEffect(() => {
    if (
      loggedIn &&
      user?.active_role === 'artisan' &&
      !isDetailsLoading &&
      userDetail &&
      profileCompletion < 80 &&
      !sessionStorage.getItem('profileNoticeShown')
    ) {
      setShowProfileNotice(true);
      sessionStorage.setItem('profileNoticeShown', 'true');
    }
  }, [loggedIn, user?.active_role, profileCompletion, isDetailsLoading, userDetail]);

  const handleCloseProfileNotice = () => {
    setShowProfileNotice(false);
  };

  const { setRefresh } = useContext(ArtisanContext);
  const [upgradingToArtisan, setUpgradingToArtisan] = useState(false);
  const { notifications } = useContext(NotificationContext);
  const navigate = useNavigate();
  const location = useLocation();
  const notificationCount = notifications.filter(
    (notification) => !notification.is_read
  ).length;
  const onSubmit = async (data) => {
    console.log(data, 'data');
    if (
      !data.primaryCraft ||
      !data.phone ||
      !data.country ||
      !data.about ||
      (!data.isFlexible &&
        (data.pricePerHour === '' || data.pricePerHour === null)) ||
      (!data.isFlexible &&
        (Number(data.pricePerHour) < 0 || Number(data.pricePerHour) > 500)) ||
      data.experience === '' ||
      data.experience === null ||
      Number(data.experience) < 0 ||
      Number(data.experience) > 30
    ) {
      if (Number(data.experience) < 0 || Number(data.experience) > 30) {
        toast.error('Experience must be between 0 and 30 years');
      } else if (
        !data.isFlexible &&
        (Number(data.pricePerHour) < 0 || Number(data.pricePerHour) > 500)
      ) {
        toast.error('Price must be between $0 and $500');
      } else {
        toast.error('Please fill all the fields correctly');
      }
      return;
    }
    setUpgradingToArtisan(true);
    try {
      const result = await upgradeToArtisanApi(data);
      console.log(result);

      // Re-fetch user data so local state reflects the new 'user_artisan' role immediately
      const { fetchUserService } =
        await import('../../global-services/user.service');
      const freshUser = await fetchUserService();
      setUser(freshUser);

      // Refresh artisan list and user details for the new role
      setRefresh((prev) => !prev);
      fetchUserDetail('artisan');

      toast.success('Artisan Upgraded successfully');
    } catch (err) {
      console.log(err);
      setUpgradingToArtisan(false);
    } finally {
      setShowUpgrade(false);
      setUpgradingToArtisan(false);
    }
  };

  const handleUpgradeToArtisan = () => {
    if (checkVerification()) {
      setShowUpgrade(true);
    }
  };

  const isDualRole = user?.role === 'user_artisan';

  let role;
  if (loggedIn) {
    role = user?.role;
  }

  return (
    <>
      <nav className="fixed top-0 left-0 right-0 z-40 bg-white/80 backdrop-blur-xl shadow-lg border-b border-gray-100 h-16 sm:h-20 overflow-visible">
        {/* Floating Centered Notice Pill with Close Button */}
        <AnimatePresence>
          {allNotices.length > 0 && (
            <div className="fixed top-16 sm:top-24 left-1/2 -translate-x-1/2 z-[100] flex flex-col items-center gap-2 w-full max-w-[95vw] sm:max-w-md pointer-events-none">
              {/* Main Notice Pill */}
              <motion.div
                initial={{ y: -20, opacity: 0, scale: 0.95 }}
                animate={{ y: 0, opacity: 1, scale: 1 }}
                exit={{ y: -20, opacity: 0, scale: 0.95 }}
                className={`
                  pointer-events-auto
                  relative flex items-center gap-2 sm:gap-3 px-3 py-1.5 sm:px-4 sm:py-2.5 rounded-full border shadow-2xl backdrop-blur-md
                  transition-all duration-300 active:scale-95 cursor-pointer group overflow-hidden
                  ${
                    allNotices.length > 1
                      ? 'bg-slate-900/95 border-slate-700 text-white shadow-indigo-500/10'
                      : allNotices[0].color === 'rose'
                        ? 'bg-rose-600/95 border-rose-400 text-white shadow-rose-500/20'
                        : allNotices[0].color === 'sky'
                          ? 'bg-sky-600/95 border-sky-400 text-white shadow-sky-500/20'
                        : allNotices[0].color === 'indigo'
                          ? 'bg-indigo-600/95 border-indigo-400 text-white shadow-indigo-500/20'
                          : 'bg-amber-500/95 border-amber-300 text-white shadow-amber-500/20'
                  }
                `}
                onClick={() => {
                  if (allNotices.length > 1) {
                    setIsNoticesExpanded(!isNoticesExpanded);
                  } else {
                    const activeNotice = allNotices[0];
                    if (activeNotice.type === 'STRIPE')
                      navigate('/artisan/settings?tab=payments');
                    if (activeNotice.type === 'DOCUMENTS')
                      navigate('/artisan/settings?tab=verification');
                    if (activeNotice.type === 'PROFILE_INCOMPLETE')
                      navigate('/artisan/profile');
                    if (
                      activeNotice.type === 'SERVER' ||
                      activeNotice.type === 'OFFLINE'
                    )
                      navigate('/support');
                    if (activeNotice.type === 'VERIFY_EMAIL') {
                      const target =
                        user?.active_role === 'artisan'
                          ? '/artisan/settings?tab=security'
                          : '/user/settings';
                      navigate(target);
                    }
                  }
                }}
              >
                {/* Background Glow for Multiple Notices */}
                {allNotices.length > 1 && (
                  <div className="absolute inset-0 bg-gradient-to-r from-indigo-500/10 via-purple-500/10 to-pink-500/10 animate-gradient-x" />
                )}

                {/* Icon Section */}
                <div className="relative bg-white/20 p-1 sm:p-1.5 rounded-full group-hover:bg-white/30 transition-colors shrink-0">
                  {allNotices.length > 1 ? (
                    <Bell size={14} className="animate-pulse" />
                  ) : (
                    <>
                      {(allNotices[0].type === 'SERVER' ||
                        allNotices[0].type === 'OFFLINE') && (
                        <AlertTriangle size={14} className="animate-pulse" />
                      )}
                      {allNotices[0].type === 'STRIPE' && (
                        <CreditCard size={14} />
                      )}
                      {allNotices[0].type === 'APPROVAL' && <Info size={14} />}
                      {allNotices[0].type === 'VERIFY_EMAIL' && (
                        <ShieldAlert size={14} />
                      )}
                      {allNotices[0].type === 'DOCUMENTS' && (
                        <FileUp size={14} />
                      )}
                      {allNotices[0].type === 'PROFILE_INCOMPLETE' && (
                        <Sparkles size={14} />
                      )}
                    </>
                  )}
                  {allNotices.length > 1 && (
                    <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-rose-500 text-[9px] font-bold text-white ring-2 ring-slate-900">
                      {allNotices.length}
                    </span>
                  )}
                </div>

                {/* Text Section */}
                <div className="relative flex flex-col leading-none min-w-0 flex-1">
                  <span className="text-[8px] sm:text-[9px] font-black uppercase tracking-[0.2em] opacity-80 truncate">
                    {allNotices.length > 1
                      ? 'Important Notices'
                      : allNotices[0].type === 'SERVER' ||
                          allNotices[0].type === 'OFFLINE'
                        ? 'Attention'
                        : 'Action Needed'}
                  </span>
                  <span className="text-[10px] sm:text-xs font-bold whitespace-nowrap truncate">
                    {allNotices.length > 1
                      ? `Critical actions pending`
                      : allNotices[0].text}
                  </span>
                </div>

                {/* Interaction Section */}
                <div className="relative flex items-center gap-2 shrink-0">
                  <div className="w-[1px] h-4 bg-white/20"></div>
                  {allNotices.length > 1 ? (
                    <div
                      className={`p-0.5 sm:p-1 rounded-full hover:bg-white/20 transition-transform ${isNoticesExpanded ? 'rotate-180' : ''}`}
                    >
                      <ChevronDown size={12} className="sm:hidden" />
                      <ChevronDown size={14} className="hidden sm:block" />
                    </div>
                  ) : (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleDismissNotice(allNotices[0].type);
                      }}
                      className="p-0.5 sm:p-1 rounded-full hover:bg-white/20 transition-colors text-white/80 hover:text-white"
                      title="Dismiss"
                    >
                      <X size={14} className="sm:hidden" />
                      <X size={16} className="hidden sm:block" />
                    </button>
                  )}
                </div>
              </motion.div>

              {/* Expanded Notices List */}
              <AnimatePresence>
                {allNotices.length > 1 && isNoticesExpanded && (
                  <motion.div
                    initial={{ height: 0, opacity: 0, y: -10 }}
                    animate={{ height: 'auto', opacity: 1, y: 0 }}
                    exit={{ height: 0, opacity: 0, y: -10 }}
                    className="pointer-events-auto w-full max-h-[60vh] overflow-y-auto custom-scrollbar bg-white/95 backdrop-blur-xl border border-gray-100 rounded-[2rem] shadow-2xl ring-1 ring-black/5"
                  >
                    <div className="p-3 space-y-2">
                      {allNotices.map((notice) => (
                        <div
                          key={notice.type}
                          onClick={() => {
                            if (notice.type === 'STRIPE')
                              navigate('/artisan/settings?tab=payments');
                            if (notice.type === 'DOCUMENTS')
                              navigate('/artisan/settings?tab=verification');
                            if (notice.type === 'PROFILE_INCOMPLETE')
                              navigate('/artisan/profile');
                            if (
                              notice.type === 'SERVER' ||
                              notice.type === 'OFFLINE'
                            )
                              navigate('/support');
                            if (notice.type === 'VERIFY_EMAIL') {
                              const target =
                                user?.active_role === 'artisan'
                                  ? '/artisan/settings?tab=security'
                                  : '/user/settings';
                              navigate(target);
                            }
                            setIsNoticesExpanded(false);
                          }}
                          className={`
                            group/item flex items-center gap-4 p-3.5 rounded-2xl transition-all cursor-pointer active:scale-[0.98]
                            ${
                              notice.color === 'rose'
                                ? 'hover:bg-rose-50 text-rose-900 border border-transparent hover:border-rose-100'
                                : notice.color === 'sky'
                                  ? 'hover:bg-sky-50 text-sky-900 border border-transparent hover:border-sky-100'
                                : notice.color === 'indigo'
                                  ? 'hover:bg-indigo-50 text-indigo-900 border border-transparent hover:border-indigo-100'
                                  : 'hover:bg-amber-50 text-amber-900 border border-transparent hover:border-amber-100'
                            }
                          `}
                        >
                          <div
                            className={`
                            p-2.5 rounded-xl shrink-0 transition-transform group-hover/item:scale-110
                            ${
                              notice.color === 'rose'
                                ? 'bg-rose-100 text-rose-600 shadow-sm shadow-rose-200/50'
                                : notice.color === 'sky'
                                  ? 'bg-sky-100 text-sky-600 shadow-sm shadow-sky-200/50'
                                : notice.color === 'indigo'
                                  ? 'bg-indigo-100 text-indigo-600 shadow-sm shadow-indigo-200/50'
                                  : 'bg-amber-100 text-amber-600 shadow-sm shadow-amber-200/50'
                            }
                          `}
                          >
                            {notice.type === 'STRIPE' && (
                              <CreditCard size={16} />
                            )}
                            {(notice.type === 'SERVER' ||
                              notice.type === 'OFFLINE') && (
                              <AlertTriangle size={16} />
                            )}
                            {notice.type === 'APPROVAL' && <Info size={16} />}
                            {notice.type === 'VERIFY_EMAIL' && (
                              <ShieldAlert size={16} />
                            )}
                            {notice.type === 'DOCUMENTS' && (
                              <FileUp size={16} />
                            )}
                            {notice.type === 'PROFILE_INCOMPLETE' && (
                              <Sparkles size={16} />
                            )}
                          </div>

                          <div className="flex-1 min-w-0">
                            <h4 className="text-[13px] font-bold leading-none">
                              {notice.text}
                            </h4>
                            <p className="text-[10px] opacity-60 font-semibold mt-1">
                              {notice.type === 'STRIPE' && 'Connect Stripe to receive payments'}
                              {notice.type === 'DOCUMENTS' && 'Verify your identity to build trust'}
                              {notice.type === 'PROFILE_INCOMPLETE' && 'Complete your profile to get more clients'}
                              {notice.type === 'VERIFY_EMAIL' && 'Check your inbox for verification link'}
                              {(notice.type === 'SERVER' || notice.type === 'OFFLINE') && 'Action required immediately'}
                              {notice.type === 'APPROVAL' && 'Awaiting administrator review'}
                            </p>
                          </div>

                          <div className="flex items-center gap-2">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleDismissNotice(notice.type);
                                if (allNotices.length <= 1)
                                  setIsNoticesExpanded(false);
                              }}
                              className="p-2 rounded-xl hover:bg-black/5 transition-colors text-gray-400 hover:text-gray-600"
                              title="Dismiss"
                            >
                              <X size={14} />
                            </button>
                            <ChevronRight
                              size={14}
                              className="text-gray-300 group-hover/item:translate-x-1 transition-transform"
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="bg-gray-50/50 px-6 py-3 border-t border-gray-100 flex items-center justify-between">
                      <span className="text-[10px] font-bold text-gray-400">
                        HandPreneur Security & Updates
                      </span>
                      <button
                        onClick={() => setIsNoticesExpanded(false)}
                        className="text-[10px] font-black text-indigo-600 uppercase tracking-widest hover:underline"
                      >
                        Close
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}
        </AnimatePresence>

        <div className="max-w-7xl mx-auto pl-2 pr-2 h-full">
          <div className="flex items-center justify-between h-full">
            {/* Mobile Menu Toggle */}
            <div className=" flex items-center -translate-x-1 sm:-translate-x-0">
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="lg:hidden p-2 rounded-lg hover:bg-pale-100 transition"
                aria-label="Toggle Menu"
              >
                {isMenuOpen ? <X size={22} /> : <Menu size={22} />}
              </button>

              {/* Logo */}
              <LogoSection />
            </div>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex lg:px-6">
              <NavbarNavigation loggedIn={loggedIn} userDetail={user} />
            </div>

            {/* Right Actions */}
            <div className="flex items-center gap-2 sm:gap-4">
              {/* Search – hide on very small screens */}

              <SearchBar />

              {/* Notifications */}
              <Notifications
                loggedIn={loggedIn}
                notificationCount={notificationCount}
              />

              {/* Account */}
              {!loading && !loggedIn && (
                <button
                  className="
      flex items-center justify-center
      px-3 py-2 text-sm
      sm:px-4 sm:py-2
      lg:px-6 lg:py-2.5 lg:text-base
      lg:hidden
      border-2 border-rose-600 text-rose-700
      font-semibold rounded-lg
      hover:bg-rose-600 hover:text-white
      transition-all
      ml-1
    "
                  onClick={() =>
                    navigate('/signin', { state: { background: location } })
                  }
                >
                  <User className="w-4 h-4 lg:w-5 lg:h-5" />
                  <span className="hidden sm:inline ml-2">Sign In</span>
                </button>
              )}

              <div className="hidden lg:block">
                <Account
                  setIsMenuOpen={setIsMenuOpen}
                  loggedIn={loggedIn}
                  user={user}
                  loading={loading}
                  userDetail={userDetail}
                  handleLogout={handleLogout}
                />
              </div>
              {/* Upgrade Button – desktop only */}
              <div className="hidden lg:block">
                <UpgradeToArtisanButton
                  loggedIn={loggedIn}
                  isDualRole={isDualRole}
                  userDetail={user}
                  handleUpgradeToArtisan={handleUpgradeToArtisan}
                />
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      <MobileMenu
        isMenuOpen={isMenuOpen}
        setIsMenuOpen={setIsMenuOpen}
        loggedIn={loggedIn}
        userDetail={user}
        isDualRole={isDualRole}
        handleUpgradeToArtisan={handleUpgradeToArtisan}
      />

      {/* Upgrade Modal */}
      {showUpgrade && (
        <Suspense fallback={null}>
          <UpgradeToArtisanModal
            open={showUpgrade}
            onClose={() => setShowUpgrade(false)}
            onSubmit={onSubmit}
            upgradingToArtisan={upgradingToArtisan}
            setUpgradingToArtisan={setUpgradingToArtisan}
          />
        </Suspense>
      )}

      <EmailVerificationModal
        isOpen={isVerifyModalOpen}
        onClose={() => setIsVerifyModalOpen(false)}
        onResend={handleResend}
        loading={verifyLoading}
      />
      <CompleteProfileModal
        isOpen={showCompleteProfile}
        onClose={handleCloseCompleteProfile}
      />

      <AnimatePresence>
        {showProfileNotice && (
          <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 sm:p-6 bg-slate-900/60 backdrop-blur-xl">
             <motion.div
               initial={{ opacity: 0 }}
               animate={{ opacity: 1 }}
               exit={{ opacity: 0 }}
               onClick={handleCloseProfileNotice}
               className="absolute inset-0"
             />
             <Notice 
                profileCompletion={profileCompletion} 
                setShowNotice={handleCloseProfileNotice}
                userDetail={userDetail}
             />
          </div>
        )}
      </AnimatePresence>
    </>
  );
}
